import { forwardRef, useRef } from 'react'
import PropTypes from 'prop-types'
import { hideVisually } from 'polished'

import theme from 'theme'
import Text from 'components/common/Text'

const InputSwitchLabel = forwardRef(({ checked, disabled, ...props }, ref) => (
  <label
    ref={ref}
    css={{
      display: 'block',
      width: theme.space[4] * 2,
      ...(disabled && { opacity: theme.opacity[0] }),
      position: 'relative',
      cursor: 'pointer',

      '&::before': {
        display: 'block',
        content: "''",
        borderRadius: theme.space[4] / 2,
        height: theme.space[4],
        width: '100%',
        backgroundColor: checked ? theme.colors.blue[0] : theme.colors.gray[2],
        transition: 'background-color 150ms, border-color 150ms'
      },

      '&::after': {
        display: 'block',
        content: "''",
        borderRadius: theme.borderRadius[2],
        height: theme.space[3] + theme.space[1] / 2,
        width: theme.space[3] + theme.space[1] / 2,
        position: 'absolute',
        top: '50%',
        transform: checked
          ? `translate3d(${theme.space[4] + theme.space[1] / 2}px, -50%, 0)`
          : `translate3d(${theme.space[1]}px, -50%, 0)`,
        transition: 'transform 150ms, border-color 150ms',
        backgroundColor: theme.colors.white[0],
        zIndex: 0
      }
    }}
    {...props}
  />
))

InputSwitchLabel.propTypes = {
  checked: PropTypes.bool,
  disabled: PropTypes.bool
}

const InputSwitch = ({
  checked,
  disabled,
  onChange,
  name,
  label,
  labelPlacement,
  ...rest
}) => {
  const labelRef = useRef()

  const Label = () =>
    label ? (
      <Text
        as="p"
        css={{
          cursor: 'pointer',
          order: labelPlacement === 'left' ? -1 : null
        }}
        onClick={() => labelRef.current.click()}
      >
        {label}
      </Text>
    ) : null

  return (
    <div
      css={{
        position: 'relative',
        display: 'grid',
        gridAutoColumns: 'auto',
        justifyContent: 'start',
        gridAutoFlow: 'column',
        gap: theme.space[2],
        alignItems: 'center'
      }}
      {...rest}
    >
      <InputSwitchLabel
        ref={labelRef}
        checked={checked}
        data-checkbox-name={name}
        disabled={disabled}
      >
        <input
          data-test-switch-input
          checked={checked || false}
          css={hideVisually()}
          disabled={disabled}
          name={name}
          type="checkbox"
          onChange={disabled ? () => {} : onChange}
        />
      </InputSwitchLabel>
      <Label />
    </div>
  )
}

InputSwitch.propTypes = {
  checked: PropTypes.bool,
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
  name: PropTypes.string,
  label: PropTypes.string,
  labelPlacement: PropTypes.oneOf(['left', 'right'])
}

InputSwitch.defaultProps = {
  labelPlacement: 'right'
}

export default InputSwitch
